import { useMemo } from "react"
import { Data, Data2, useDataTypesHooks } from "../../../../../features/data-types"
import { Column, FieldType, O, Section, Table, UIPermission } from "../../../../../features/ui"
import { Discount } from "../../../../common"
import { Item, OperationItemsTable, usePricesHooks } from "../../../../operations"
import { useSystemStateHooks } from "../../../../system"

type Props = {
    itemsData: Partial<Item>[]
    createMode?: boolean
    supplierId?: string
    readOnly?: boolean
    onCreateItem?: () => void
    onEditItem?: (productId: string) => void
    onRemoveItem?: (productId: string) => void
}

export const PurchaseItemTable = (props: Props) => {
    const { itemsData, createMode, supplierId, readOnly, onCreateItem, onEditItem, onRemoveItem } = props

    const { userHasPermission } = useSystemStateHooks()
    const canReadPurchasePrice = userHasPermission(UIPermission.READ_PRODUCT_PURCHASE_PRICE)
    const pricesHooks = usePricesHooks()
    const { formatPercentage, formatPrice} = useDataTypesHooks()

    const hasDiscounts = useMemo(() => itemsData.some(item => item.specialDiscountYield || (item.discounts || []).length > 0), [itemsData])

    const renderDetail = (row: Data2) => {
        const productId = row.productLotId! as string
        const basePrice = pricesHooks.product.purchaseBasePrice(productId)
        const discounts = row.discounts as O<Discount[]> || []
        const specialDiscountYield = row.specialDiscountYield as O<number>
        
        const bonusColumns = discounts.map(bonus => ({
            name: `bonus-${bonus.name}`,
            type: FieldType.TEXT,
            label: bonus.name,
        }))
        const specialBonusColumn = specialDiscountYield ? [{
            name: 'specialBonus',
            type: FieldType.TEXT,
            label: 'Bonificación Especial'
        }] : []
        const detailColumns: Column[] = [
            {
                name: 'basePrice',
                type: FieldType.PRICE,
                label: 'Precio Base'
            },
            ...bonusColumns,
            ...specialBonusColumn,
            {
                name: 'discountedPrice',
                type: FieldType.PRICE,
                label: 'Precio de Compra'
            },
            {
                name: 'totalPrice',
                type: FieldType.PRICE,
                label: 'Precio Total'
            }
        ]
        
        const discountsMap: Map<string, [number, number]> = new Map(discounts.map(bonus => [bonus.name, [bonus.yield, pricesHooks.adjustment.calculate(basePrice, bonus.yield)]]))
        const discountsAmount = Array.from(discountsMap.values()).map(([_, amount]) => amount).reduce((acc, amount) => acc + amount, 0)
        const priceWithDiscounts = basePrice - discountsAmount
        const detailRow: Data2 = {
            productLotId: productId,
            basePrice,
            ...(specialDiscountYield && {
                specialBonus: `(${formatPercentage(-specialDiscountYield)}) ${formatPrice(pricesHooks.adjustment.calculate(priceWithDiscounts, specialDiscountYield))}`
            }),
            discountedPrice: row.specialDiscountUnitSubtotal,
            totalPrice: row.totalPrice
        }
        discountsMap.forEach(([_yield, amount], name) => {
            detailRow[`bonus-${name}`] = `(${formatPercentage(-_yield)}) ${formatPrice(amount)}`
        })

        return (
            <Section
                id="purchase-item-table-detail"
                title="Detalle de Precio"
                space={12}
            >
                <Table
                    columns={detailColumns}
                    rows={[detailRow] as Data[]}
                    rowId="productLotId"
                    alignRight={false}
                />
            </Section>
        )
    }
    return (
        <OperationItemsTable
            itemsData={itemsData}
            createMode={createMode}
            priceLabel="Precio de Compra"
            discountLabel="Bonificaciones"
            disableCreate={!supplierId}
            readOnly={readOnly}
            onCreateItem={onCreateItem}
            onEditItem={onEditItem}
            onRemoveItem={onRemoveItem}
            renderDetail={canReadPurchasePrice && hasDiscounts ? renderDetail : undefined}
        />
    )
}
